import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { startBrowserLogout } from '@utils/kratos.utils'
import { clearStoredChallenge } from '@utils/oauth-challenge-stash.utils'

interface SignOutHook {
  signOut: () => void
  error: string | null
  pending: boolean
}

export const useSignOut = (): SignOutHook => {
  const intl = useIntl()
  // Read intl through a ref inside the async catch so a language switch mid-
  // flight produces the message in the locale the user is reading *now*, not
  // the one captured at signOut() call time. Sync through useEffect rather
  // than during render — concurrent-render-safe and matches React docs'
  // textbook pattern. There's a 1-render window between mount and effect
  // commit where intlRef.current is the initial value, but signOut() can't
  // realistically fire that early (no UI to click yet).
  const intlRef = useRef(intl)
  useEffect(() => {
    intlRef.current = intl
  }, [intl])

  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const signOut = (): void => {
    setError(null)
    setPending(true)
    // Stale stash would replay against the now-defunct Hydra request.
    clearStoredChallenge()
    startBrowserLogout().catch((err: unknown) => {
      const fallback = intlRef.current.formatMessage({ id: 'signOut.error.fallback' })
      setError(err instanceof Error ? err.message : fallback)
      setPending(false)
    })
    // Success path navigates away → component unmounts → no setPending(false) needed.
  }

  return { signOut, error, pending }
}
